import { memo, useEffect, useRef, type RefObject } from 'react'
import { createChart, LineSeries } from 'lightweight-charts'
import type { IChartApi } from 'lightweight-charts'
import { useLivePrice } from '@/hooks/useLivePrice'
import { useGameStore } from '@/store/gameStore'
import { fetchKlines } from '@/lib/binanceApi'

/** Начало текущего часа в секундах (для часовой свечи) */
function currentHourStart(): number {
  return Math.floor(Date.now() / 1000 / 3600) * 3600
}

interface ChartWithHeatmapProps {
  rightBarOffset?: number
  dotRef?: RefObject<HTMLDivElement | null>
  onChartReady?: (chart: IChartApi, series: import('lightweight-charts').ISeriesApi<'Line'> | import('lightweight-charts').ISeriesApi<'Candlestick'>) => void
  onChartDestroy?: () => void
  onLastCandleTime?: (time: number) => void
  /** Вызывается после загрузки данных и fitContent — момент для центрирования */
  onChartFitted?: (chart: IChartApi) => void
}

export const ChartWithHeatmap = memo(function ChartWithHeatmap({ dotRef, onChartReady, onChartDestroy, onLastCandleTime, onChartFitted }: ChartWithHeatmapProps) {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartApiRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<import('lightweight-charts').ISeriesApi<'Line'> | null>(null)
  const lastPriceRef = useRef<{ time: import('lightweight-charts').UTCTimestamp; value: number } | null>(null)
  const lastCandleRef = useRef<{ open: number; high: number; low: number } | null>(null)
  const lastHourRef = useRef<number | null>(null)
  const price = useLivePrice()
  const { currentCrypto } = useGameStore()

  useEffect(() => {
    if (!chartRef.current) return

    const chart = createChart(chartRef.current, {
      autoSize: true,
      layout: {
        background: { color: 'transparent' },
        textColor: '#94a3b8',
      },
      grid: { vertLines: { visible: false }, horzLines: { color: 'rgba(148,163,184,0.1)' } },
      rightPriceScale: { visible: false },
      leftPriceScale: { borderVisible: false, visible: true },
      handleScale: {
        mouseWheel: false,
        pinch: false,
        axisPressedMouseMove: { time: false, price: true },
        axisDoubleClickReset: false,
      },
      handleScroll: {
        mouseWheel: false,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      timeScale: {
        visible: true,
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 48,
      },
      crosshair: { vertLine: { visible: false }, horzLine: { visible: false } },
    })

    const lineSeries = chart.addSeries(LineSeries, {
      priceScaleId: 'left',
      color: '#cd7f32',
      lineWidth: 2,
      crosshairMarkerVisible: false,
      lastValueVisible: true,
      pointMarkersVisible: false,
    })

    const updateDot = () => {
      const pt = lastPriceRef.current
      const dot = dotRef?.current
      if (!dot || !pt) return
      const xCoord = chart.timeScale().timeToCoordinate(pt.time)
      const yCoord = lineSeries.priceToCoordinate(pt.value)
      if (xCoord != null && yCoord != null) {
        const psw = chart.priceScale('left').width()
        dot.style.left = `${psw + xCoord}px`
        dot.style.top = `${yCoord}px`
        dot.style.display = 'block'

      } else {
        dot.style.display = 'none'
      }
    }
    chart.timeScale().subscribeVisibleLogicalRangeChange(updateDot)

    // Опрос ценовой шкалы: updateDot при вертикальном зуме / setVisibleRange
    const priceScale = chart.priceScale('left')
    let lastPrFrom = -1
    let lastPrTo = -1
    let priceTickRaf: number
    const priceTick = () => {
      try {
        const pr = priceScale.getVisibleRange()
        if (pr && (pr.from !== lastPrFrom || pr.to !== lastPrTo)) {
          lastPrFrom = pr.from
          lastPrTo = pr.to
          updateDot()
        }
      } catch {
        // chart may be dismounted or not ready
      }
      priceTickRaf = requestAnimationFrame(priceTick)
    }
    priceTickRaf = requestAnimationFrame(priceTick)

    let destroyed = false

    const loadData = async () => {
      try {
        const klines = await fetchKlines(currentCrypto, '1h', 240)
        if (destroyed) return
        const data = klines.map((k) => ({
          time: k.time as import('lightweight-charts').UTCTimestamp,
          value: k.close,
        }))
        lineSeries.setData(data)
        chart.timeScale().fitContent()
        onChartFitted?.(chart)

        if (data.length) {
          const allHigh = Math.max(...klines.map((k) => k.high))
          const allLow = Math.min(...klines.map((k) => k.low))
          const mid = (allHigh + allLow) / 2
          const halfSpan = (allHigh - allLow) / 2
          const isMobile = window.innerWidth < 768
          const scale = isMobile ? 0.83 * 2 : 0.83
          const range = { minValue: mid - halfSpan * scale, maxValue: mid + halfSpan * scale }
          lineSeries.applyOptions({
            autoscaleInfoProvider: () => ({ priceRange: range }),
          })
        }
        requestAnimationFrame(() => {
          if (destroyed) return
          chart.priceScale('left').applyOptions({ autoScale: false })
        })

        const last = data.length ? data[data.length - 1] : null
        if (last) {
          lastCandleRef.current = { open: last.value, high: last.value, low: last.value }
          lastHourRef.current = last.time as number
          onLastCandleTime?.(last.time as number)
        } else {
          lastCandleRef.current = null
        }
      } catch {
        lineSeries.setData([])
        lastCandleRef.current = null
      }
    }
    void loadData()

    // Блокируем wheel полностью — zoom только через pinch и перетаскивание осей
    const el = chartRef.current
    const onWheel = (e: WheelEvent) => { e.preventDefault() }
    el.addEventListener('wheel', onWheel, { passive: false })

    chartApiRef.current = chart
    seriesRef.current = lineSeries
    onChartReady?.(chart, lineSeries as import('lightweight-charts').ISeriesApi<'Line'>)

    return () => {
      destroyed = true
      const dot = dotRef?.current
      if (dot) dot.style.display = 'none'
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(updateDot)
      cancelAnimationFrame(priceTickRaf)
      el.removeEventListener('wheel', onWheel)
      seriesRef.current = null
      lastPriceRef.current = null
      lastCandleRef.current = null
      lastHourRef.current = null
      chart.remove()
      chartApiRef.current = null
      onChartDestroy?.()
    }
  }, [currentCrypto, onLastCandleTime, onChartFitted])

  useEffect(() => {
    if (!seriesRef.current || price === null) return
    const series = seriesRef.current

    const hourStart = currentHourStart() as import('lightweight-charts').UTCTimestamp
    const hourChanged = lastHourRef.current !== null && lastHourRef.current !== hourStart
    if (hourChanged) {
      lastCandleRef.current = null
      onLastCandleTime?.(hourStart)
    }
    lastHourRef.current = hourStart

    const point = { time: hourStart, value: price }
    lastCandleRef.current = { open: price, high: price, low: price }
    series.update(point)
    lastPriceRef.current = { time: hourStart, value: price }

    const dot = dotRef?.current
    if (dot && chartApiRef.current) {
      const xCoord = chartApiRef.current.timeScale().timeToCoordinate(hourStart)
      const yCoord = series.priceToCoordinate(price)
      if (xCoord != null && yCoord != null) {
        const psw = chartApiRef.current.priceScale('left').width()
        dot.style.left = `${psw + xCoord}px`
        dot.style.top = `${yCoord}px`
        dot.style.display = 'block'
      }
    }

  }, [price, onLastCandleTime])

  return (
    <div className="flex h-full min-h-[200px] w-full flex-col overflow-hidden">
      <div ref={chartRef} className="h-full min-h-[180px] w-full flex-1" />
    </div>
  )
})
